import React, { useEffect, useRef, useState } from "react";
import { Send, X } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { chatWithMia } from "../lib/api";
import MiaMatchCard from "./MiaMatchCard";

const MIA_AVATAR =
  "https://static.prod-images.emergentagent.com/jobs/d4e9e42c-b36d-4cef-b2b6-e711b533d4ca/images/0d293aba995c1c7ed667a64bec64e8c2c41d1476261d6dc4bc3f934d793dcb89.png";

function getSessionId() {
  if (typeof window === "undefined") return Math.random().toString(36).slice(2);
  let s = window.localStorage.getItem("mia_session");
  if (!s) {
    s = `mia-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    window.localStorage.setItem("mia_session", s);
  }
  return s;
}

export default function MiaWidget() {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: t.mia.welcome },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(getSessionId);
  const scrollRef = useRef(null);

  // Update welcome when language changes
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 1 && prev[0].role === "assistant") {
        return [{ role: "assistant", content: t.mia.welcome }];
      }
      return prev;
    });
  }, [t.mia.welcome]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const res = await chatWithMia(sessionId, text, lang);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: res.reply, match: res.match || null },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            lang === "nl"
              ? "Excuses, ik ben momenteel niet bereikbaar. Probeer het later opnieuw."
              : lang === "de"
              ? "Entschuldigung, ich bin derzeit nicht erreichbar. Versuchen Sie es später erneut."
              : "Apologies, I'm temporarily unavailable. Please try again later.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher — compact circular avatar, expands on click */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={t.mia.open}
          title={t.mia.open}
          className="fixed bottom-6 left-6 sm:bottom-8 sm:left-8 z-50 group transition-transform hover:scale-105"
          data-testid="mia-launcher"
        >
          <span
            className="block w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden ring-1 ring-gold/70 shadow-lg bg-charcoal-surface"
            style={{ boxShadow: "0 6px 20px rgba(45, 30, 14, 0.25)" }}
          >
            <img
              src={MIA_AVATAR}
              alt="Mia"
              className="w-full h-full object-cover"
            />
          </span>
          <span
            className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-cream"
            aria-hidden="true"
          />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-8 left-8 z-50 w-[92vw] max-w-[400px] h-[78vh] max-h-[640px] bg-charcoal-surface border border-gold/30 flex flex-col shadow-2xl"
          data-testid="mia-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-cream/10 bg-charcoal-deep">
            <div className="flex items-center gap-3">
              <img
                src={MIA_AVATAR}
                alt="Mia"
                className="w-10 h-10 object-cover"
                style={{ borderRadius: 0 }}
              />
              <div className="leading-tight">
                <p className="font-serif text-lg text-cream">{t.mia.title}</p>
                <p className="text-[0.65rem] tracking-[0.22em] uppercase text-gold">
                  <span className="inline-block w-1.5 h-1.5 bg-emerald-400 mr-2 align-middle"></span>
                  {t.mia.subtitle}
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-cream-muted hover:text-gold p-1"
              aria-label={t.mia.close}
              data-testid="mia-close"
            >
              <X size={18} strokeWidth={1.25} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
            data-testid="mia-messages"
          >
            {messages.map((m, i) => (
              <div key={i} className="space-y-2">
                <div
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[78%] px-4 py-2.5 text-[0.92rem] leading-[1.55] whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-gold text-charcoal-surface"
                        : "bg-charcoal text-cream border border-cream/10"
                    }`}
                    data-testid={`mia-msg-${m.role}`}
                  >
                    {m.content}
                  </div>
                </div>
                {m.role === "assistant" && m.match && m.match.property && (
                  <MiaMatchCard match={m.match} lang={lang} onClose={() => setOpen(false)} />
                )}
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-charcoal text-cream-muted text-[0.92rem] px-4 py-2.5 border border-cream/10">
                  {t.mia.thinking}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-cream/10 p-3 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t.mia.placeholder}
              className="input-luxury flex-1"
              data-testid="mia-input"
              disabled={sending}
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              className="bg-gold hover:bg-gold-hover text-charcoal-surface px-4 py-3 transition-colors disabled:opacity-50"
              aria-label={t.mia.send}
              data-testid="mia-send"
            >
              <Send size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
